/* eslint-disable react/jsx-no-duplicate-props */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { OutlinedTextFieldProps, TextField } from '@mui/material';
import { usePhoneFormatter } from '@europrocurement/flexy-components/hooks/usePhoneFormatter';
import './inputs.css';

export type FlexyTextFieldProps = OutlinedTextFieldProps;

/**
 * Ce composant permet l'affichage d'un input au style personnalisé.
 *
 * ## Utilisation
 * Il hérite directement du [composant TextField de Material UI](https://mui.com/material-ui/react-text-field/), et s'utilise donc de la même façon.
 *
 * ## Fonctionnalités spéciales
 * - **Type "tel"** : formate automatiquement la saisie en numéro de téléphone
 *   - Supporte les formats nationaux (XX XX XX XX XX) et internationaux (+XX X XX XX XX XX)
 *   - Le formatage s'applique en temps réel pendant la saisie
 *   - Seuls les chiffres et le caractère + (en début) sont acceptés
 * - **Type "number"** : validation et outils numériques
 *   - Seuls les chiffres, point décimal, et signe moins sont autorisés
 *   - Support de la molette pour incrémenter/décrémenter
 *   - Flèches haut/bas natives pour navigation
 *   - Respect des propriétés min, max, step
 */
export const FlexyTextField: React.FunctionComponent<FlexyTextFieldProps> = styled(
    (props: FlexyTextFieldProps) => {
        const {
            InputProps: externalInputProps,
            inputProps: externalInputPropsNative,
            type: inputType,
            value: externalValue,
            ...restProps
        } = props;
        const [internalValue, setInternalValue] = React.useState<string>(
            externalValue?.toString() || props.defaultValue?.toString() || '',
        );

        const isPhoneType = props.type === 'tel';
        const isNumberType = props.type === 'number';

        const phoneFormatter = usePhoneFormatter(isPhoneType ? internalValue : undefined);

        // Helper pour valider les caractères numériques
        const isValidNumberCharacter = (
            key: string,
            currentValue: string,
            cursorPosition: number,
        ) => {
            // Touches de contrôle toujours autorisées
            if (
                [
                    'Backspace',
                    'Delete',
                    'Tab',
                    'Escape',
                    'Enter',
                    'Home',
                    'End',
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                ].includes(key)
            ) {
                return true;
            }

            // Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            if (key === 'a' || key === 'c' || key === 'v' || key === 'x') {
                return true;
            }

            // Chiffres
            if (/^[0-9]$/.test(key)) {
                return true;
            }

            // Point décimal (seulement un autorisé)
            if (key === '.' && !currentValue.includes('.') && !currentValue.includes(',')) {
                return true;
            }

            // Signe minus (seulement au début)
            if (key === '-' && cursorPosition === 0 && !currentValue.startsWith('-')) {
                return true;
            }

            return false;
        };

        // Injecte une valeur dans l'input et déclenche le onChange de React
        const setNativeInputValue = (input: HTMLInputElement, value: string) => {
            const nativeSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                'value',
            )?.set;
            nativeSetter?.call(input, value);
            input.dispatchEvent(new Event('input', { bubbles: true }));
        };

        // Helper pour valider le contenu collé (virgule acceptée comme séparateur décimal)
        const isValidNumberPaste = (pastedText: string) => {
            const normalizedText = pastedText.trim().replace(',', '.');
            const numberRegex = /^-?(\d+\.?\d*|\.\d+)$/;
            return numberRegex.test(normalizedText);
        };

        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            let newValue = event.target.value;
            // Filet de sécurité (autofill, IME) : normalise virgule → point
            // Setter sans dispatchEvent pour éviter un double appel onChange
            if (isNumberType && newValue.includes(',')) {
                newValue = newValue.replace(',', '.');
                const nativeSetter = Object.getOwnPropertyDescriptor(
                    window.HTMLInputElement.prototype,
                    'value',
                )?.set;
                nativeSetter?.call(event.target, newValue);
            }
            // Pour type="number" internalValue n'est jamais affiché (pas de value prop),
            // évite un re-render inutile qui pourrait affecter le focus via les hooks MUI.
            if (!isNumberType) {
                setInternalValue(newValue);
            }

            if (props.onChange) {
                props.onChange(event);
            }
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (isPhoneType) {
                const currentValue = (event.target as HTMLInputElement).value;
                const cursorPosition = (event.target as HTMLInputElement).selectionStart || 0;

                if (!phoneFormatter.isValidKey(event.key, cursorPosition, currentValue)) {
                    event.preventDefault();
                }
            }

            // Validation pour le type number
            if (isNumberType && !event.ctrlKey && !event.metaKey) {
                const input = event.target as HTMLInputElement;
                const currentValue = input.value;
                const cursorPosition = input.selectionStart || 0;

                // Virgule → remplace immédiatement par un point
                if (event.key === ',') {
                    event.preventDefault();
                    if (!currentValue.includes('.') && !currentValue.includes(',')) {
                        const start = input.selectionStart ?? currentValue.length;
                        const end = input.selectionEnd ?? currentValue.length;
                        const newValue = `${currentValue.slice(0, start)}.${currentValue.slice(end)}`;
                        setNativeInputValue(input, newValue);
                        requestAnimationFrame(() => input.setSelectionRange(start + 1, start + 1));
                    }
                    return;
                }

                if (!isValidNumberCharacter(event.key, currentValue, cursorPosition)) {
                    event.preventDefault();
                }
            }

            if (props.onKeyDown) {
                props.onKeyDown(event);
            }
        };

        const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
            if (isPhoneType) {
                const pastedText = event.clipboardData.getData('text');
                const currentValue = (event.target as HTMLInputElement).value;
                const cursorPosition = (event.target as HTMLInputElement).selectionStart || 0;

                if (!phoneFormatter.isValidPaste(pastedText, cursorPosition, currentValue)) {
                    event.preventDefault();
                    return;
                }
            }

            // Validation pour le type number — normalise virgule → point si présente
            if (isNumberType) {
                const pastedText = event.clipboardData.getData('text');
                if (!isValidNumberPaste(pastedText)) {
                    event.preventDefault();
                    return;
                }
                if (pastedText.includes(',')) {
                    event.preventDefault();
                    const input = event.target as HTMLInputElement;
                    const start = input.selectionStart ?? 0;
                    const end = input.selectionEnd ?? input.value.length;
                    const normalized = pastedText.replace(',', '.');
                    const newValue =
                        input.value.slice(0, start) + normalized + input.value.slice(end);
                    setNativeInputValue(input, newValue);
                    requestAnimationFrame(() =>
                        input.setSelectionRange(
                            start + normalized.length,
                            start + normalized.length,
                        ),
                    );
                    return;
                }
            }

            if (props.onPaste) {
                props.onPaste(event);
            }
        };

        // 🎡 Gestion de la molette pour les champs number
        const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
            if (isNumberType && document.activeElement === event.target) {
                const input = event.target as HTMLInputElement;
                const currentValue = parseFloat(input.value) || 0;
                const step = parseFloat(props.inputProps?.step?.toString() || '1');
                const min = props.inputProps?.min
                    ? parseFloat(props.inputProps.min.toString())
                    : undefined;
                const max = props.inputProps?.max
                    ? parseFloat(props.inputProps.max.toString())
                    : undefined;

                let newValue = currentValue;

                // Molette vers le haut = incrément, vers le bas = décrément
                if (event.deltaY < 0) {
                    newValue = currentValue + step;
                } else {
                    newValue = currentValue - step;
                }

                // Respect des limites min/max
                if (min !== undefined && newValue < min) {
                    newValue = min;
                }
                if (max !== undefined && newValue > max) {
                    newValue = max;
                }

                // Mettre à jour la valeur
                const syntheticEvent = {
                    ...event,
                    target: { ...input, value: newValue.toString() },
                } as React.ChangeEvent<HTMLInputElement>;

                if (props.onChange) {
                    props.onChange(syntheticEvent);
                }

                event.preventDefault();
            }

            if (props.onWheel) {
                props.onWheel(event);
            }
        };

        React.useEffect(() => {
            if (!isNumberType && externalValue !== undefined && externalValue !== null) {
                setInternalValue(externalValue.toString());
            }
        }, [externalValue, isNumberType]);

        // Configuration des inputProps selon le type
        const getInputProps = () => {
            if (isPhoneType) {
                return {
                    ...externalInputPropsNative,
                    inputMode: 'tel' as const,
                    pattern: '[+]?[0-9 ]*',
                    maxLength: phoneFormatter.maxLength,
                };
            }

            if (isNumberType) {
                return {
                    ...externalInputPropsNative,
                    inputMode: 'decimal' as const,
                    pattern: '-?[0-9]*[.]?[0-9]*',
                };
            }

            return externalInputPropsNative;
        };

        const displayValue = isPhoneType ? phoneFormatter.formattedValue : internalValue;

        return (
            <TextField
                {...restProps}
                {...(!isNumberType && { value: isPhoneType ? displayValue : externalValue })}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onWheel={handleWheel}
                onBlur={(e) => {
                    if (props.onBlur)
                        props.onBlur(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
                }}
                inputProps={getInputProps()}
                InputProps={{
                    ...externalInputProps,
                    sx: {
                        ...externalInputProps?.sx,
                    },
                }}
                type={isNumberType ? 'text' : inputType}
            />
        );
    },
)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper,
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
    },
    '& .MuiOutlinedInput-input.Mui-disabled': {
        backgroundColor: 'transparent',
    },
    '& .MuiOutlinedInput-input.Mui-disabled::-webkit-input-placeholder': {
        color: theme.palette.text.secondary,
        opacity: '1',
    },
    '& input[type="number"]': {
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            opacity: 0.7,
            cursor: 'pointer',
        },
        '&:focus::-webkit-outer-spin-button, &:focus::-webkit-inner-spin-button': {
            opacity: 1,
        },
    },
}));
